import {
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/Icon';
import { OverlayOwnerContext, focusableElements } from '@/components/overlay';

export interface TableHeaderAction {
    id: string;
    label: string;
    icon: string;
    onClick: () => void;
}

export interface TableHeaderActionsProps {
    actions: TableHeaderAction[];
    position?: 'left' | 'right';
}

export const TableHeaderActions = ({
    actions,
    position = 'right',
}: TableHeaderActionsProps) => {
    const { t } = useTranslation('global');
    const owner = useContext(OverlayOwnerContext);
    const [open, setOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
    const [coordinates, setCoordinates] = useState<React.CSSProperties>({});
    const trigger = useRef<HTMLButtonElement>(null);
    const popup = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const close = (restore = true): void => {
        if(!open || isClosing){
            return;
        }
        setIsClosing(true);
        if(restore){
            trigger.current?.focus();
        }
        if(closeTimer.current){
            clearTimeout(closeTimer.current);
        }
        closeTimer.current = setTimeout(() => {
            setOpen(false);
            setIsClosing(false);
        }, 160);
    };

    useEffect(() => {
        return () => {
            if(closeTimer.current){
                clearTimeout(closeTimer.current);
            }
        };
    }, []);

    useLayoutEffect(() => {
        if(!open || !popup.current || !trigger.current){
            return;
        }
        const anchor = trigger.current.getBoundingClientRect();
        const box = popup.current.getBoundingClientRect();
        const below = window.innerHeight - anchor.bottom - 16;
        const shouldPlaceTop = below < box.height && anchor.top - 16 >= box.height;
        const targetPlacement = shouldPlaceTop ? 'top' : 'bottom';
        setPlacement(targetPlacement);

        const left = position === 'right' ? anchor.right - box.width : anchor.left;
        const boundedLeft = Math.max(8, Math.min(left, window.innerWidth - box.width - 8));

        if(targetPlacement === 'top'){
            setCoordinates({
                bottom: Math.round(window.innerHeight - anchor.top + 6),
                left: Math.round(boundedLeft),
            });
            focusableElements(popup.current)[0]?.focus();
            return;
        }

        setCoordinates({
            top: Math.round(anchor.bottom + 6),
            left: Math.round(boundedLeft),
        });
        focusableElements(popup.current)[0]?.focus();
    }, [open, position]);

    useEffect(() => {
        if(!open || isClosing){
            return;
        }
        const move = (event: Event): void => {
            if(!popup.current?.contains(event.target as Node)){
                close();
            }
        };
        window.addEventListener('resize', move);
        window.addEventListener('scroll', move, true);
        return () => {
            window.removeEventListener('resize', move);
            window.removeEventListener('scroll', move, true);
        };
    }, [open, isClosing]);

    const keyboard = (event: React.KeyboardEvent): void => {
        const items = popup.current ? focusableElements(popup.current) : [];
        const current = items.indexOf(document.activeElement as HTMLElement);
        if(event.key === 'Escape'){
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if(['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)){
            event.preventDefault();
            const next =
                event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
            items[next]?.focus();
        }
    };

    if(!actions.length){
        return null;
    }

    const animationClass = isClosing
        ? placement === 'top'
            ? 'select-dropdown--closing-top'
            : 'select-dropdown--closing-bottom'
        : placement === 'top'
          ? 'select-dropdown--open-top'
          : 'select-dropdown--open-bottom';

    return (
        <>
            <button
                ref={trigger}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t('table.actions')}
                title={t('table.actions')}
                onClick={(e) => {
                    e.stopPropagation();
                    if(isClosing){
                        return;
                    }
                    if(open){
                        close();
                        return;
                    }
                    setOpen(true);
                }}
                onKeyDown={(event) => {
                    if(event.key === 'ArrowDown'){
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${open ? 'bg-slate-200 text-slate-900 dark:bg-white/15 dark:text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10'}`}
            >
                <Icon value="moreVertical" size={16} />
            </button>
            {(open || isClosing) &&
                createPortal(
                    <>
                        <div
                            className="fixed inset-0 z-[120000] bg-transparent cursor-default"
                            data-overlay-owner={owner || undefined}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                close();
                            }}
                        />
                        <div
                            ref={popup}
                            role="menu"
                            aria-label={t('table.actions')}
                            data-overlay-owner={owner || undefined}
                            onKeyDown={keyboard}
                            style={{
                                position: 'fixed',
                                zIndex: 120001,
                                ...coordinates,
                            }}
                            className={`select-dropdown ${animationClass} flex flex-col bg-white dark:bg-dark-box rounded-lg border border-slate-200/90 dark:border-white/10 shadow-2xl overflow-hidden p-1 min-w-44 max-w-[min(18rem,calc(100vw-1rem))]`}
                        >
                            {actions.map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    role="menuitem"
                                    className="flex min-h-9 w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors text-slate-700 hover:bg-slate-100 dark:text-zinc-200 dark:hover:bg-white/10"
                                    onClick={() => {
                                        close();
                                        action.onClick();
                                    }}
                                >
                                    <Icon value={action.icon} size={16} className="shrink-0" />
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>
                    </>,
                    document.body
                )}
        </>
    );
};
